import type { ColumnDef } from '@tanstack/react-table'
import type { ICountry } from '@/entities/country'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LocationBannerCell,
  LocationBannerEditButton,
} from '@/features/location/location-banner-cell'
import { LocationStatusBadge } from '@/features/location/location-status-badge'
import { DataTable } from '@/shared/ui/data-table'

interface CountryListTableProps {
  countries: ICountry[]
  loading: boolean
  selectedCountryId: string
  onSelect: (country: ICountry) => void
  onEditBanner: (country: ICountry) => void
  onPreviewBanner: (url: string) => void
}

/**
 * Country list. Clicking a row selects the country whose provinces are shown below;
 * the action button edits the country banner.
 */
export function CountryListTable({
  countries,
  loading,
  selectedCountryId,
  onSelect,
  onEditBanner,
  onPreviewBanner,
}: CountryListTableProps) {
  const { t } = useTranslation()

  const columns = useMemo<ColumnDef<ICountry>[]>(
    () => [
      {
        id: 'banner',
        header: t('location.banner'),
        cell: ({ row }) => (
          <LocationBannerCell banner={row.original.banner} onPreview={onPreviewBanner} />
        ),
      },
      {
        accessorKey: 'code',
        header: t('location.code'),
        cell: ({ row }) => (
          <span
            className={
              row.original.id === selectedCountryId ? 'font-semibold text-blue-700' : undefined
            }
          >
            {row.original.code}
          </span>
        ),
      },
      { accessorKey: 'nativeName', header: t('location.nativeName') },
      {
        id: 'name',
        header: t('location.englishName'),
        cell: ({ row }) => row.original.name ?? '—',
      },
      {
        accessorKey: 'phoneCode',
        header: t('location.phoneCode'),
        cell: ({ row }) => row.original.phoneCode || '—',
      },
      {
        id: 'status',
        header: () => <div className="text-center">{t('common.status')}</div>,
        cell: ({ row }) => <LocationStatusBadge isActive={row.original.isActive} />,
      },
      {
        id: 'actions',
        size: 64,
        header: () => null,
        cell: ({ row }) => <LocationBannerEditButton onClick={() => onEditBanner(row.original)} />,
      },
    ],
    [onEditBanner, onPreviewBanner, selectedCountryId, t],
  )

  return (
    <DataTable
      columns={columns}
      data={countries}
      loading={loading}
      emptyText={t('common.noData')}
      onRowClick={onSelect}
    />
  )
}
